import * as React from "react";
import { Canceler } from "axios";
import { connect } from "react-redux";
import { pick } from "lodash";
import { toast } from "react-toastify";

import {
  ILinksProps,
  ILinksState,
  IAllFilters,
  IPossibleFilters,
} from "./types";
import { IStore } from "src/services/store";

import { MarketContext } from "src/services/contexts/markets/MarketContext";
import { Icons, Button, Pagination, Tooltip, Select } from "src/components";
import { scrollToElement } from "src/services/scrolling";
import { isOnlyMyLinksUser } from "src/services/authorizeUser";
import { allDefaultFilters } from "./services/filters";
import API from "src/services/api";
import SingleLink from "./components/SingleLink";
import Filters from "./components/Filters";
import Search from "./components/Search";
import { LightPrimaryBg } from "./components/LightPrimaryBg";
import { IOption } from "src/components/Form/Select";
import {
  setLinksPerPage,
  setViewType,
} from "src/services/settings/actionCreators";
import { getParameterNames } from "src/services/links/getParameterNames";
import { calculateIDWidth } from "src/services/links/calculateIDWidth";
import { calculateTableWidth } from "src/services/links/calculateTableWidth";
import Container from "src/components/Container";

class Links extends React.Component<ILinksProps, ILinksState> {
  static contextType = MarketContext;
  context!: React.ContextType<typeof MarketContext>;
  canUpdateState = false;
  cancelFiltersFetch: Canceler | null = null;
  cancelFetch: Canceler | null = null;

  initialFilters = {
    ...allDefaultFilters,
    linksPerPage: this.props.linksPerPage,
    myLinks: this.props.isOnlyMyLinksUser,
  };

  linksPerPageOptions = [
    { value: 10, label: "10" },
    { value: 20, label: "20" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
  ];

  state: ILinksState = {
    pageCount: 1,
    isLoading: true,
    isLoadingFilters: true,
    isFiltering: false,
    filters: this.initialFilters,
    parameters: [],
    extendedParameters: [],
    links: [],
    parameterNames: [],
    tableWidth: 0,
    IDWidth: 0,
    markets: [],
    exportingExcel: false,
    exportingCsv: false,
    parameterOrder: [],
  };
  getBaseFilters = () =>
    pick(this.state.filters, [
      "dateFrom",
      "dateTo",
      "showArchive",
      "myLinks",
      "parameters",
      "linksPerPage",
    ]);

  containerRef: React.RefObject<HTMLDivElement> = React.createRef();

  async componentDidMount() {
    const { location } = this.props;
    const initialSearch = location.state && location.state.initialSearch;

    if (initialSearch) {
      this.updateSearchTerm(initialSearch);
      await this.updateLinks();
    }

    this.canUpdateState = true;
    const markets = this.context.visibilityProfiles;

    const { getData } = API.ADMIN.PARAMETERS.get();
    const parameters = await getData();
    this.initFetchFilters();
    const { links, pageCount } = await this.fetchLinks(this.state.filters);

    if (this.canUpdateState) {
      this.setState(
        {
          links,
          pageCount,
          isLoading: false,
          markets,
          parameterOrder: parameters.map((p) => ({
            name: p.name,
            order: p.orderRender,
          })),
        },
        this.updateColumnWidths
      );
    }
  }

  componentWillUnmount() {
    this.canUpdateState = false;
    if (this.cancelFiltersFetch) {
      this.cancelFiltersFetch();
    }
    if (this.cancelFetch) {
      this.cancelFetch();
    }
  }

  updateColumnWidths() {
    const { links, parameterOrder } = this.state;
    const { profileParameterName } = this.props;

    const parameterNames = getParameterNames(
      links,
      parameterOrder,
      profileParameterName
    );
    const IDWidth = calculateIDWidth(links);
    this.setState({
      parameterNames,
      IDWidth,
      tableWidth: calculateTableWidth(parameterNames, 10 + IDWidth),
    });
  }

  async initFetchFilters() {
    if (this.cancelFiltersFetch) {
      this.cancelFiltersFetch();
    }

    const { profileParameterName, archiveFilters } = this.props;
    const markets = this.context.visibilityProfiles;

    const marketFilter = {
      name: profileParameterName,
      values: markets.map((market) => ({
        value: market.id,
        label: market.name,
      })),
    };

    const { getData, cancel } = API.LINKS.getFilters({
      archiveFilters,
      marketFilter,
    });
    this.cancelFiltersFetch = cancel;
    const filters = await getData();
    this.cancelFiltersFetch = null;

    this.setState({ ...filters, isLoadingFilters: false });
  }

  async fetchLinks(filters: IAllFilters) {
    if (this.cancelFetch) {
      this.cancelFetch();
    }
    const markets = this.context.visibilityProfiles;
    const { getData, cancel } = API.LINKS.getArchive(
      filters,
      markets,
      this.props.profileParameterName
    );
    this.cancelFetch = cancel;
    const links = await getData();
    this.cancelFetch = null;
    return links;
  }
  updateLinks = async () => {
    this.scrollToListTop();
    this.setState({ isFiltering: true });
    const { links, pageCount } = await this.fetchLinks(this.state.filters);

    this.setState(
      {
        links,
        pageCount,
        isFiltering: false,
      },
      this.updateColumnWidths
    );
  };

  updateFilters = (filters: IPossibleFilters, stopFetch?: boolean) => {
    this.setState(
      (state) => ({
        filters: { ...state.filters, ...filters, page: 1 },
      }),
      !stopFetch ? this.updateLinks : undefined
    );
  };
  clearFilters = () => {
    this.updateFilters(this.initialFilters);
  };

  updateSearchTerm = (searchTerm: string) => {
    this.setState((state) => ({
      filters: { ...state.filters, searchTerm },
    }));
  };

  loadPage = (page: number) => {
    if (this.state.filters.page === page) {
      return;
    }
    this.scrollToListTop();
    this.setState(
      (state) => ({ isFiltering: true, filters: { ...state.filters, page } }),
      this.updateLinks
    );
  };

  scrollToListTop = () => {
    if (this.containerRef.current) {
      scrollToElement(this.containerRef.current);
    }
  };

  changeViewType = () => {
    this.props.setViewType(this.props.viewType !== "list" ? "list" : "table");
  };

  changeLinksPerPage = (option: IOption<number>) => {
    this.props.setLinksPerPage(option.value);
    this.setState(
      (state) => ({
        filters: {
          ...state.filters,
          linksPerPage: option.value,
        },
      }),
      this.updateLinks
    );
  };

  renderLoading() {
    return (
      <div className="mt-12">
        {Array.from({ length: 10 }).map((_, index) => (
          <div
            key={index}
            className="loader bg-grey rounded mb-10 flex-1 h-64"
          />
        ))}
      </div>
    );
  }

  duplicateLink = (id: number, visibleId: string) => () => {
    const location = {
      pathname: "/link-create",
      state: { duplicateId: id, duplicateVisibleId: visibleId },
    };

    this.props.history.push(location);
  };

  exportCsv = async () => {
    this.setState({ exportingCsv: true });
    await API.LINKS.EXPORT.csv(this.state.filters);
    if (this.canUpdateState) {
      this.setState({ exportingCsv: false });
    }
  };
  exportExcel = async () => {
    this.setState({ exportingExcel: true });
    await API.LINKS.EXPORT.excel(this.state.filters);
    if (this.canUpdateState) {
      this.setState({ exportingExcel: false });
    }
  };

  toggleLinkArchive = (linkId: number, isArchiving: boolean) => async () => {
    try {
      this.setState(
        ({ links }) => ({
          links: links.filter((link) => link.id !== linkId),
        }),
        () => {
          if (this.state.links.length <= 1 && this.state.pageCount > 1) {
            this.updateLinks();
          }
        }
      );

      if (isArchiving) {
        await API.LINKS.archiveLink(linkId).getData();
      } else {
        await API.LINKS.restoreLink(linkId).getData();
      }
    } catch (error) {
      toast.error(
        `${isArchiving ? "Archiving" : "Publishing"} failed: ${error.message}`
      );
    }
  };

  renderLinks() {
    const {
      links,
      isFiltering,
      filters,
      pageCount,
      exportingExcel,
      exportingCsv,
      parameterNames,
      tableWidth,
      IDWidth,
    } = this.state;
    const { viewType } = this.props;
    const currentPage = filters.page || 1;
    const selectedLinksPerPage = this.linksPerPageOptions.find(
      (option) => option.value === filters.linksPerPage
    );
    return (
      <>
        <div className="relative z-10">
          <Pagination
            current={currentPage}
            count={pageCount}
            onChange={this.loadPage}
            className="mb-4"
          />
          <div className="archive-list-options absolute z-100 flex items-start">
            <div className="group py-2 px-3 border border-transparent text-right rounded mr-3 hover:bg-grey hover:border-white hover:shadow-md">
              Export all <Icons.External size={16} className="ml-1" />
              <div className="hidden group-hover:block border-t mt-2 pt-2 border-grey-light">
                <Button
                  size="small"
                  background="grey"
                  className="flex-1"
                  onClick={this.exportCsv}
                  loading={exportingCsv}
                  disabled={exportingCsv}
                >
                  CSV
                </Button>
                <Button
                  size="small"
                  background="grey"
                  className="flex-1 ml-1"
                  onClick={this.exportExcel}
                  loading={exportingExcel}
                  disabled={exportingExcel}
                >
                  Excel
                </Button>
              </div>
            </div>
            <Tooltip
              text={`Switch to ${viewType !== "list" ? "list" : "table"} view`}
              className="mr-6"
            >
              <button
                className="flex leading-default border-white overflow-hidden border rounded-lg transition-shadow focus:outline-none focus:shadow-outline hover:shadow-md"
                onClick={this.changeViewType}
              >
                <span
                  className={`px-2 py-2 ${
                    viewType === "list" ? "bg-grey-dark" : ""
                  }`}
                >
                  <Icons.ListView size={16} className="ml-1" />
                </span>
                <span
                  className={`px-2 py-2 ${
                    viewType === "table" ? "bg-grey-dark" : ""
                  }`}
                >
                  <Icons.TableView size={16} className="ml-1" />
                </span>
              </button>
            </Tooltip>
            <Select
              name="linksPerPage"
              options={this.linksPerPageOptions}
              isClearable={false}
              value={selectedLinksPerPage}
              onChange={this.changeLinksPerPage}
              secondary={true}
              className="w-32"
            />
          </div>
        </div>
        <div
          className={viewType === "table" ? "max-w-full overflow-x-auto" : ""}
        >
          <div style={{ width: viewType === "table" ? `${tableWidth}em` : "" }}>
            {viewType === "table" && (
              <div className="flex sticky pin-t bg-white">
                <LightPrimaryBg
                  className="p-2 border-r border-white"
                  style={{ width: "10rem" }}
                >
                  Created on
                </LightPrimaryBg>
                <LightPrimaryBg
                  className="p-2 border-r border-white"
                  style={{ width: `${IDWidth}em` }}
                >
                  ID
                </LightPrimaryBg>
                {parameterNames.map(({ name, length }) => (
                  <LightPrimaryBg
                    key={name}
                    className="p-2 border-r border-white"
                    style={{ width: `${length}em` }}
                  >
                    {name}
                  </LightPrimaryBg>
                ))}
              </div>
            )}
            {links.map((link) => (
              <SingleLink
                key={link.id}
                link={link}
                isLoading={isFiltering}
                viewType={viewType}
                parameterNames={parameterNames}
                IDWidth={IDWidth}
                shortenerEnabled={this.props.shortenerEnabled}
                targetUrlParameterName={this.props.targetUrlParameterName}
                displayNameParameters={this.props.displayNameParameters}
                duplicateLink={this.duplicateLink(link.id, link.visibleId)}
                toggleArchived={this.toggleLinkArchive(
                  link.id,
                  !filters.showArchive
                )}
                isArchived={filters.showArchive}
              />
            ))}
          </div>
        </div>
        <Pagination
          current={currentPage}
          count={pageCount}
          onChange={this.loadPage}
          className="mt-4"
        />
      </>
    );
  }

  render() {
    const {
      isLoadingFilters,
      isLoading,
      isFiltering,
      filters,
      links,
      parameters,
      extendedParameters,
    } = this.state;
    return (
      <Container ref={this.containerRef}>
        <Search
          value={filters.searchTerm || ""}
          onChange={this.updateSearchTerm}
          onSubmit={this.updateLinks}
          isSearching={isFiltering}
        />

        <div className="flex relative">
          <Filters
            filters={this.getBaseFilters()}
            isFiltering={isFiltering}
            isLoading={isLoadingFilters}
            parameters={parameters}
            extendedParameters={extendedParameters}
            onChange={this.updateFilters}
            onSubmit={this.updateLinks}
            onReset={this.clearFilters}
            myLinksOnly={this.props.isOnlyMyLinksUser}
          />

          <div className="flex-1 overflow-hidden pl-16 py-2">
            {isLoading ? (
              this.renderLoading()
            ) : links.length > 0 ? (
              this.renderLinks()
            ) : (
              <div className="text-lg text-center my-8">
                <h2 className="my-8">
                  <Icons.Circle
                    character="!"
                    color="secondary"
                    bgFill={true}
                    className="mb-1 mr-2"
                  />{" "}
                  No matching links found
                </h2>
                <p>Please change the active filters and try again</p>
              </div>
            )}
          </div>
        </div>
      </Container>
    );
  }
}

export default connect(
  ({
    user: { roles },
    settings: {
      shortenerEnabled,
      profileParameterName,
      targetUrlParameterName,
      parameterNameUsedInArchiveEntryHeader,
      archiveFilters,
      archiveViewType,
      archiveLinksPerPage,
    },
  }: IStore) => ({
    isOnlyMyLinksUser: isOnlyMyLinksUser(roles),
    shortenerEnabled,
    profileParameterName,
    targetUrlParameterName,
    displayNameParameters: parameterNameUsedInArchiveEntryHeader,
    archiveFilters,
    viewType: archiveViewType,
    linksPerPage: archiveLinksPerPage,
  }),
  (dispatch) => ({
    setViewType: (viewType: string) => dispatch(setViewType(viewType)),
    setLinksPerPage: (n: number) => dispatch(setLinksPerPage(n)),
  })
)(Links);



// WEBPACK FOOTER //
// ./src/views/Links/Links.tsx